// Couche de stockage offline (IndexedDB via idb).
// Cinq stores qui reflètent le client offline-first d'Atelier :
//   boards     — base de connaissances board curée (rails/mesures/symptômes)
//   faults     — miroir local des pannes du core (PULL de sync)
//   events     — file des diagnostics terrain à remonter (PUSH de sync)
//   sync_state — curseur + dernière sync
//   identity   — identité Ed25519 du poste (clé privée + client_id)

import { type DBSchema, type IDBPDatabase, openDB } from "idb";
import type { BoardModel, DiagnosticEvent, Fault, SyncState } from "../domain/types";

export interface StoredIdentity {
  id: "singleton";
  private_key_hex: string; // Ed25519 32 octets
  public_key_hex: string;
  client_id: string | null; // uuid attribué par le core à l'enrôlement
}

interface ThorDiagDB extends DBSchema {
  boards: { key: string; value: BoardModel };
  faults: {
    key: string;
    value: Fault;
    indexes: { by_domaine: string; by_model: string };
  };
  events: {
    key: string; // client_event_id
    value: DiagnosticEvent;
    indexes: { by_synced: string };
  };
  sync_state: { key: string; value: SyncState };
  identity: { key: string; value: StoredIdentity };
  meta: { key: string; value: { key: string; value: string } };
}

const DB_NAME = "thor-diagnostique";
const DB_VERSION = 1;

let _dbp: Promise<IDBPDatabase<ThorDiagDB>> | null = null;

export function db(): Promise<IDBPDatabase<ThorDiagDB>> {
  if (_dbp === null) {
    _dbp = openDB<ThorDiagDB>(DB_NAME, DB_VERSION, {
      upgrade(d) {
        d.createObjectStore("boards", { keyPath: "id" });

        const faults = d.createObjectStore("faults", { keyPath: "id" });
        faults.createIndex("by_domaine", "domaine");
        faults.createIndex("by_model", "device_model");

        const events = d.createObjectStore("events", { keyPath: "client_event_id" });
        // IndexedDB n'indexe pas les booléens : on indexe une chaîne "0"/"1".
        events.createIndex("by_synced", "synced");

        d.createObjectStore("sync_state", { keyPath: "id" });
        d.createObjectStore("identity", { keyPath: "id" });
        d.createObjectStore("meta", { keyPath: "key" });
      },
    });
  }
  return _dbp;
}

// ── Boards ────────────────────────────────────────────────────────────────
export async function putBoard(b: BoardModel): Promise<void> {
  await (await db()).put("boards", b);
}
export async function listBoards(): Promise<BoardModel[]> {
  return (await db()).getAll("boards");
}
export async function getBoard(id: string): Promise<BoardModel | undefined> {
  return (await db()).get("boards", id);
}
export async function deleteBoard(id: string): Promise<void> {
  await (await db()).delete("boards", id);
}

// ── Faults ────────────────────────────────────────────────────────────────
export async function upsertFault(f: Fault): Promise<void> {
  await (await db()).put("faults", f);
}
export async function faultsByDomaine(domaine: string): Promise<Fault[]> {
  return (await db()).getAllFromIndex("faults", "by_domaine", domaine);
}
export async function countFaults(): Promise<number> {
  return (await db()).count("faults");
}

// ── Events ──────────────────────────────────────────────────────────────-─
export async function addEvent(e: DiagnosticEvent): Promise<void> {
  await (await db()).put("events", e);
}
export async function getEvent(id: string): Promise<DiagnosticEvent | undefined> {
  return (await db()).get("events", id);
}
export async function updateEvent(e: DiagnosticEvent): Promise<void> {
  await (await db()).put("events", e);
}
export async function pendingEvents(limit = 500): Promise<DiagnosticEvent[]> {
  const all = await (await db()).getAll("events");
  return all.filter((e) => !e.synced).slice(0, limit);
}

// ── Sync state ──────────────────────────────────────────────────────────-─
export async function getSyncState(): Promise<SyncState> {
  const s = await (await db()).get("sync_state", "singleton");
  return s ?? { id: "singleton", faults_cursor: null, boards_cursor: null, last_sync_at: null };
}
export async function putSyncState(s: SyncState): Promise<void> {
  await (await db()).put("sync_state", s);
}

// ── Identity ────────────────────────────────────────────────────────────-─
export async function getIdentity(): Promise<StoredIdentity | undefined> {
  return (await db()).get("identity", "singleton");
}
export async function putIdentity(i: StoredIdentity): Promise<void> {
  await (await db()).put("identity", i);
}

// ── Meta (clé/valeur : licence, etc.) ──────────────────────────────────────
export async function getMeta(key: string): Promise<string | null> {
  const r = await (await db()).get("meta", key);
  return r ? r.value : null;
}
export async function setMeta(key: string, value: string): Promise<void> {
  await (await db()).put("meta", { key, value });
}
